import styles from "./AppAccordion.module.scss";
import { Accordion, AccordionTab, AccordionTabChangeEvent } from "primereact/accordion";
import { classNames } from "primereact/utils";
import { ReactNode, useState } from "react";

export interface IAppAccordionTab {
  id: string | number;
  header: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  headerClassName?: string;
  contentClassName?: string;
}

export type AppAccordionVariant = "light" | "dark";

interface IAppAccordionProps {
  tabs: IAppAccordionTab[];
  multiple?: boolean;
  activeIndex?: number | number[] | null;
  defaultActiveIndex?: number | number[] | null;
  onTabChange?: (e: AccordionTabChangeEvent) => void;
  className?: string;
  expandIcon?: string;
  collapseIcon?: string;
  /** Theme variant: "light" for admin pages, "dark" for wizard/portal pages */
  variant?: AppAccordionVariant;
}

export const AppAccordion = ({
  tabs,
  multiple = true,
  activeIndex,
  defaultActiveIndex,
  onTabChange,
  className,
  expandIcon = "pi pi-chevron-down",
  collapseIcon = "pi pi-chevron-up",
  variant = "light",
}: IAppAccordionProps) => {
  const [internalActiveIndex, setInternalActiveIndex] = useState<number | number[] | null>(
    defaultActiveIndex ?? (multiple ? [] : null)
  );

  const isControlled = activeIndex !== undefined;
  const currentActiveIndex = isControlled ? activeIndex : internalActiveIndex;

  const handleTabChange = (e: AccordionTabChangeEvent) => {
    if (!isControlled) {
      setInternalActiveIndex(e.index);
    }
    onTabChange?.(e);
  };

  return (
    <Accordion
      multiple={multiple}
      activeIndex={currentActiveIndex}
      onTabChange={handleTabChange}
      className={classNames(
        styles.accordion,
        variant === "dark" && styles.accordion_dark,
        className
      )}
      expandIcon={expandIcon}
      collapseIcon={collapseIcon}
      transitionOptions={{ timeout: 300, classNames: "p-toggleable-content" }}
    >
      {tabs.map((tab) => (
        <AccordionTab
          key={tab.id}
          header={tab.header}
          disabled={tab.disabled}
          headerClassName={classNames(styles.accordion_header, tab.headerClassName)}
          contentClassName={classNames(styles.accordion_content, tab.contentClassName)}
        >
          {tab.content}
        </AccordionTab>
      ))}
    </Accordion>
  );
};
